import React from 'react';
import { FormattedMessage } from 'react-intl';

import colors from 'config/theme/colors';
import {
  CHAT_STATISTICS_URL,
  CHECK_LIST_ADVICE_STATISTICS_URL,
  INTERVENTIONS_STATISTICS_URL, PARTS_CHAT_STATISTICS_URL, PARTS_CHECKLIST_ADVICE_STATISTICS_URL,
  PARTS_INTERVENTION_STATISTICS_URL
} from 'constants/ApiConstants';
import { AuditQuestionTypeEnum } from 'modules/audits/types/resources/audits.enum';
import { CHAT_CHART_LEGEND_NAME } from 'modules/statistics/selectors/utils';
import { createDictionary } from 'utils/ObjectUtils';

import { CHANNEL } from './ChatConstants';
import {
  ANSWERED,
  OPEN,
  CLOSED,
  READ,
  SENT,
  ACCEPTED,
  TO_BE_SENT,
  IN_PROGRESS,
  INTERVENTION_MODE
} from './InterventionConstants';
import { PARTS, SALES, TECHNICIAN } from './ModeConstants';
import { DEFAULT_PARTS_MODULES, DEFAULT_SALES_MODULES, DEFAULT_TECHNICIAN_MODULES, REMINDER, PARTS_REMINDER } from './ModuleConstants';

import { PATH } from './RoutesConstants';
import type { FeaturesEnum } from '../modules/global/typing/enums/features.enum';
import type { ModeEnum } from '../modules/global/typing/enums/modes.enum';

/** @type {Array<import("@arhs-fleetback/types/entities/interventions").InterventionStatus>} */
export const STATS_STATUS = [ANSWERED, OPEN, CLOSED, READ, SENT, TO_BE_SENT]; // all but DELETED & IN_PROGRESS
/** @type {Array<import("@arhs-fleetback/types/entities/interventions").InterventionStatus>} */
export const STATS_STATUS_BEFORE_CLOSE = STATS_STATUS.filter(status => status !== CLOSED);

const ALL_STATS_MODULES = [...DEFAULT_TECHNICIAN_MODULES, ...DEFAULT_SALES_MODULES, ...DEFAULT_PARTS_MODULES];

export const getStatsModules = (mode: ModeEnum, isSelfService: boolean, features: FeaturesEnum[] = []) => {
  const hasReminder = features.includes(REMINDER) && !isSelfService;
  const hasPartsReminder = features.includes(PARTS_REMINDER);

  const MODULES_CONFIG = createDictionary(
    {
      [INTERVENTION_MODE.TECHNICIAN]: [...DEFAULT_TECHNICIAN_MODULES, ...(hasReminder ? [REMINDER] : [])],
      [INTERVENTION_MODE.SALES]: DEFAULT_SALES_MODULES,
      [INTERVENTION_MODE.PARTS]: [...DEFAULT_PARTS_MODULES, ...(hasPartsReminder ? [PARTS_REMINDER] : [])]
    },
    [...ALL_STATS_MODULES, ...(hasReminder ? [REMINDER] : []), ...(hasPartsReminder ? [PARTS_REMINDER] : [])]
  );

  return MODULES_CONFIG[mode];
};

export const COLORS_PIE_CHART = {
  answered: '#9D26AD',
  sentOn: '#1699EF',
  readOn: '#3E54B1',
  yes: '#22bf37',
  ok: '#22bf37',
  no: '#da1a00',
  nok: '#da1a00',
  none: '#37b98b',
  low: '#dcb746',
  high: '#ce2c53',
  call_me_back: '#1699EF',
  acceptedOn: '#4Ba581',
  rejectedOn: '#C42137',
  toHide: 'rgba(255, 255, 255, 0)',
  rejected: '#C42137',
  accepted: '#4Ba581',
  unanswered: '#1699EF',
  notSent: '#E91E63FF',
  [CHANNEL.EMAIL]: '#033059',
  [CHANNEL.SMS]: '#d9663a',
  [CHANNEL.INTERNAL]: '#f2caa7',
  [CHANNEL.WHATSAPP]: '#94CD68',
  [CHAT_CHART_LEGEND_NAME.PHOTOS]: '#833DA6',
  [CHAT_CHART_LEGEND_NAME.VIDEOS]: '#2581AC',
  [CHAT_CHART_LEGEND_NAME.FILES]: '#8280E0'
};

export const ClosedQuestionsKeys = {
  YES: 'YES',
  NO: 'NO',
  UNANSWERED: 'UNANSWERED',
  YES_ALREADY_KNOWN: 'YES_ALREADY_KNOWN'
} as const;
type ClosedQuestionsKeysEnum = (typeof ClosedQuestionsKeys)[keyof typeof ClosedQuestionsKeys];

export const ResponseSubsetsKeys = {
  ALREADY_KNOWN: 'ALREADY_KNOWN',
  NOT_ASKED: 'NOT_ASKED'
} as const;
type ResponseSubsetsKeysEnum = (typeof ResponseSubsetsKeys)[keyof typeof ResponseSubsetsKeys];

export const DEFAULT_QUESTION_COLORS: Record<ClosedQuestionsKeysEnum, string> = {
  YES: colors['@success-color'],
  NO: colors['@error-color'],
  UNANSWERED: '#B9BCBF',
  YES_ALREADY_KNOWN: colors['@grey-base']
};

export const DEFAULT_SUBSET_COLORS: Record<ResponseSubsetsKeysEnum, string> = {
  ALREADY_KNOWN: colors['@grey-base'],
  NOT_ASKED: colors['@grey-dark']
};

export const QUESTION_OTHER_COLORS = [
  '#8280E0',
  '#C37DE6',
  '#B9A386',
  '#2C2B6B',
  '#7F4A00',
  '#9D26AD',
  '#FFB040',
  '#00C7BE',
  '#FC0',
  '#404040'
];

// Muted/neutral palette for unknown subset keys (e.g. BRAND_NEW_SUBSET).
// Kept intentionally desaturated so subsets stay visually subordinate to parents.
export const SUBSET_OTHER_COLORS = [
  colors['@text-color-secondary'],
  colors['@grey-light'],
  '#7B8C99',
  '#8C7B6A',
  '#6A8C7B',
  '#7B6A8C',
  '#9E9E8C',
  '#8C9E9E'
];

export const COLORS_BAR_CHART = {
  answered: '#9D26AD',
  sentOn: '#1699EF',
  count: '#888888',
  acceptedOn: '#4Ba581',
  acceptedFormally: '#4Ba581',
  acceptedManually: '#9e6818',
  rejectedOn: '#C42137',
  proposed: '#4Ba581',
  accepted: '#1699EF',
  unanswered: '#1699EF',
  current_period: '#9D26AD',
  sentIntervention: '#A9A9A9',
  hasCustomerMessages: '#1699EF',
  hasInternalMessages: '#dcb746',
  checkInSent: colors['@checkIn-sent'],
  checkOutSent: colors['@checkOut-sent'],
  homeCheckInCompleted: colors['@homeCheckIn-completed'],
  homeCheckOutCompleted: colors['@homeCheckOut-completed'],
  onSiteOrKioskCheckInCompleted: colors['@checkIn-completed'],
  onSiteOrKioskCheckOutCompleted: colors['@checkOut-completed'],
  manualCheckOutCompleted: colors['@checkOut-manually-completed']
};

export const AUDIT_STATISTICS_QUESTIONS = [
  AuditQuestionTypeEnum.AUTHOR,
  AuditQuestionTypeEnum.BRAND,
  AuditQuestionTypeEnum.EMPLOYEE,
  AuditQuestionTypeEnum.CHECKBOX,
  AuditQuestionTypeEnum.EXPLODED_VIEW,
  AuditQuestionTypeEnum.KPI,
  AuditQuestionTypeEnum.MULTI_EMPLOYEE,
  AuditQuestionTypeEnum.RADIO_BUTTON,
  AuditQuestionTypeEnum.SEVERITY,
  AuditQuestionTypeEnum.YES_NO,
  AuditQuestionTypeEnum.NUMBER,
  AuditQuestionTypeEnum.SEVERITY
];

export const AUDIT_STATISTICS_DETAILED_EXPORT_LIMIT = 100;

export const CATEGORY_STATISTICS_NO_CATEGORY = 'no_category';

export const DAY = 'day';
export const WEEK = 'week';
export const MONTH = 'month';
export const QUARTER = 'quarter';
export const YEAR = 'year';

export const SELECTED_TYPE_GLOBAL = 'GLOBAL';
export const SELECTED_TYPE_DETAILS = 'DETAILS';

const A_TO_Z_SORT = 'A_TO_Z';
const Z_TO_A_SORT = 'Z_TO_A';
const ASC_PROPOSED_AMOUNT = 'ASC_PROPOSED_AMOUNT';
const DESC_PROPOSED_AMOUNT = 'DESC_PROPOSED_AMOUNT';
const ASC_ACCEPTED_AMOUNT = 'ASC_ACCEPTED_AMOUNT';
const DESC_ACCEPTED_AMOUNT = 'DESC_ACCEPTED_AMOUNT';

export const SORT_BY = {
  A_TO_Z_SORT,
  Z_TO_A_SORT,
  ASC_PROPOSED_AMOUNT,
  DESC_PROPOSED_AMOUNT,
  ASC_ACCEPTED_AMOUNT,
  DESC_ACCEPTED_AMOUNT
};

const SHOW_ALL_BRAND = 'SHOW_ALL_BRAND';
const SHOW_BRAND_ONLY_WITH_AMOUNT = 'SHOW_BRAND_ONLY_WITH_AMOUNT';
export const TYPE_OF_VIEW = {
  SHOW_ALL_BRAND,
  SHOW_BRAND_ONLY_WITH_AMOUNT
};

export const ExportTypeEnum = {
  BO_EXPORT: 'BO_EXPORT',
  RAW_EXPORT: 'RAW_EXPORT',
  RAW_MEDIA_EXPORT: 'RAW_MEDIA_EXPORT'
} as const;

export type ExportTypeEnum = (typeof ExportTypeEnum)[keyof typeof ExportTypeEnum];

export const CONTRIBUTOR_TYPE = {
  AUTHOR: {
    value: 'AUTHOR',
    id: 'createdById',
    name: 'createdByName'
  },
  SENDER: {
    value: 'SENDER',
    id: 'sentById',
    name: 'sentByName'
  }
};

export const PROVIDED = 'PROVIDED';
export const CONFIRMED = 'CONFIRMED';
export const REFUSED = 'REFUSED';
export const CANCELED = 'CANCELED';
export const TO_VALIDATE = 'TO_VALIDATE';

export const AUTOMATIC_VALIDATION_PROVIDED = 'AUTOMATIC_VALIDATION_PROVIDED';
export const AUTOMATIC_AUTOMATICALLY_ACCEPTED = 'AUTOMATIC_AUTOMATICALLY_ACCEPTED';
export const AUTOMATIC_MANUALLY_ACCEPTED = 'AUTOMATIC_MANUALLY_ACCEPTED';
export const MANUALLY_REFUSED = 'MANUALLY_REFUSED';

export const COLORS_AUDIT_IN_FILES_PROGRESS = {
  total: '#73B1E4',
  completed: '#0072CE'
};

export const COLORS_STATUS_REPARTITION = {
  [TO_BE_SENT]: '#E3F0FF',
  [SENT]: '#73B1E4',
  [IN_PROGRESS]: '#0072CE',
  [ANSWERED]: '#003763',
  [PROVIDED]: '#E3F0FF',
  [AUTOMATIC_VALIDATION_PROVIDED]: '#E3F0FF',
  [ACCEPTED]: '#73B1E4',
  [AUTOMATIC_AUTOMATICALLY_ACCEPTED]: '#73B1E4',
  [AUTOMATIC_MANUALLY_ACCEPTED]: '#0072CE',
  [REFUSED]: '#0072CE',
  [MANUALLY_REFUSED]: '#0072CE',
  [TO_VALIDATE]: '#003763',
  [CANCELED]: '#003763',
  [CONFIRMED]: '#0072CE',
  manualCheckOutCompleted: '#003763'
};

export const DEALER_TABLE_TRANSLATIONS = createDictionary(
  {
    [PARTS]: {
      accepted: { id: 'statistics_dealer.table.parts.help.accepted_amount', defaultMessage: 'Cost of accepted parts offers' },
      conversionRatio: {
        id: 'statistics_dealer.table.parts.help.conversion_rate',
        defaultMessage: 'Ratio between accepted files and sent files with parts offers'
      },
      manuallyAccepted: { id: 'statistics_dealer.table.parts.help.acceptedManuallyAmount', defaultMessage: 'Cost of manually accepted parts offers' },
      proposed: { id: 'statistics_dealer.table.parts.help.proposed_amount', defaultMessage: 'Cost of sent parts offers' }
    },
    [SALES]: {
      accepted: { id: 'statistics_dealer.table.sales.help.accepted_amount', defaultMessage: 'Cost of accepted offers' },
      conversionRatio: { id: 'statistics_dealer.table.sales.help.conversion_rate', defaultMessage: 'Ratio between accepted files and sent files with offers' },
      manuallyAccepted: { id: 'statistics_dealer.table.sales.help.acceptedManuallyAmount', defaultMessage: 'Cost of manually accepted offers' },
      proposed: { id: 'statistics_dealer.table.sales.help.proposed_amount', defaultMessage: 'Cost of sent offers' }
    }
  },
  {
    accepted: { id: 'statistics_dealer.table.help.accepted_amount', defaultMessage: 'Cost of accepted works' },
    conversionRatio: { id: 'statistics_dealer.table.help.conversion_rate', defaultMessage: 'Ratio between accepted files and sent files with works' },
    manuallyAccepted: { id: 'statistics_dealer.table.help.acceptedManuallyAmount', defaultMessage: 'Cost of manually accepted works' },
    proposed: { id: 'statistics_dealer.table.help.proposed_amount', defaultMessage: 'Cost of sent works' }
  }
);

export const PATH_DETAILS = {
  [INTERVENTION_MODE.TECHNICIAN]: PATH.STATS_AFTER_SALES_DETAILS,
  [INTERVENTION_MODE.SALES]: PATH.STATS_SALES_DETAILS,
  [INTERVENTION_MODE.PARTS]: PATH.STATS_PARTS_DETAILS
};

export const MANUFACTURER_PATH_DETAILS = {
  [INTERVENTION_MODE.TECHNICIAN]: PATH.MANUFACTURER_STATS_AFTER_SALES_DETAILS,
  [INTERVENTION_MODE.SALES]: PATH.MANUFACTURER_STATS_SALES_DETAILS,
  [INTERVENTION_MODE.PARTS]: PATH.MANUFACTURER_STATS_PARTS_DETAILS
};

export const PATH_DASHBOARD = {
  [INTERVENTION_MODE.TECHNICIAN]: PATH.STATS_AFTER_SALES,
  [INTERVENTION_MODE.SALES]: PATH.STATS_SALES,
  [INTERVENTION_MODE.PARTS]: PATH.STATS_PARTS
};

export const MANUFACTURER_PATH_DASHBOARD = {
  [INTERVENTION_MODE.SALES]: PATH.MANUFACTURER_STATS_SALES,
  [INTERVENTION_MODE.PARTS]: PATH.MANUFACTURER_STATS_PARTS,
  [INTERVENTION_MODE.TECHNICIAN]: PATH.MANUFACTURER_STATS_AFTER_SALES
};

export const STATS_PATHS_DETAILS = {
  [INTERVENTION_MODE.SALES]: [
    PATH_DETAILS[INTERVENTION_MODE.SALES],
    MANUFACTURER_PATH_DETAILS[INTERVENTION_MODE.SALES]
  ],
  [INTERVENTION_MODE.PARTS]: [
    PATH_DETAILS[INTERVENTION_MODE.PARTS],
    MANUFACTURER_PATH_DETAILS[INTERVENTION_MODE.PARTS]
  ],
  [INTERVENTION_MODE.TECHNICIAN]: [
    PATH_DETAILS[INTERVENTION_MODE.TECHNICIAN],
    MANUFACTURER_PATH_DETAILS[INTERVENTION_MODE.TECHNICIAN],
    PATH.SELF_SERVICE_STATS_DETAILS,
    PATH.MANUFACTURER_STATS_SELF_SERVICE_DETAILS
  ]
};

export const STATS_PATHS_DASHBOARD = {
  [INTERVENTION_MODE.SALES]: [
    PATH_DASHBOARD[INTERVENTION_MODE.SALES],
    MANUFACTURER_PATH_DASHBOARD[INTERVENTION_MODE.SALES]
  ],
  [INTERVENTION_MODE.PARTS]: [
    PATH_DASHBOARD[INTERVENTION_MODE.PARTS],
    MANUFACTURER_PATH_DASHBOARD[INTERVENTION_MODE.PARTS]
  ],
  [INTERVENTION_MODE.TECHNICIAN]: [
    PATH_DASHBOARD[INTERVENTION_MODE.TECHNICIAN],
    MANUFACTURER_PATH_DASHBOARD[INTERVENTION_MODE.TECHNICIAN],
    PATH.SELF_SERVICE_STATS_DASHBOARD,
    PATH.MANUFACTURER_STATS_SELF_SERVICE
  ]
};

/** @type {Record<import('modules/statistics/types').StatisticModeReducer, Array<string>> */
export const ALL_STATS_PATHS_BY_MODE = {
  [INTERVENTION_MODE.SALES]: [
    PATH_DASHBOARD[INTERVENTION_MODE.SALES],
    MANUFACTURER_PATH_DASHBOARD[INTERVENTION_MODE.SALES],
    ...STATS_PATHS_DETAILS[INTERVENTION_MODE.SALES]
  ],
  [INTERVENTION_MODE.PARTS]: [
    PATH_DASHBOARD[INTERVENTION_MODE.PARTS],
    MANUFACTURER_PATH_DASHBOARD[INTERVENTION_MODE.PARTS],
    ...STATS_PATHS_DETAILS[INTERVENTION_MODE.PARTS]
  ],
  [INTERVENTION_MODE.TECHNICIAN]: [
    PATH.SELF_SERVICE_STATS_DASHBOARD,
    PATH.MANUFACTURER_STATS_SELF_SERVICE,
    PATH_DASHBOARD[INTERVENTION_MODE.TECHNICIAN],
    MANUFACTURER_PATH_DASHBOARD[INTERVENTION_MODE.TECHNICIAN],
    ...STATS_PATHS_DETAILS[INTERVENTION_MODE.TECHNICIAN]
  ]
};

export const ALL_STATS_PATHS = [
  ...(Object.values(PATH_DASHBOARD)),
  ...(Object.values(MANUFACTURER_PATH_DASHBOARD)),
  PATH.SELF_SERVICE_STATS_DASHBOARD,
  PATH.MANUFACTURER_STATS_SELF_SERVICE
];

export const DATE_FIELDS = {
  SEND: 'sendDate',
  START: 'startDate',
  CREATED_ON: 'createdOn'
} as const;

export const REQUEST_DATE_FORMATTER = {
  [DATE_FIELDS.START]: {
    mode: [TECHNICIAN],
    label: <FormattedMessage id="startDate" defaultMessage="Start date" />
  },
  [DATE_FIELDS.CREATED_ON]: {
    mode: [SALES, PARTS],
    label: <FormattedMessage id="page.stats.creationDate" defaultMessage="Creation date" />,
    transform: (from: string, to: string) => ({ from, to }),
  },
  [DATE_FIELDS.SEND]: {
    sort: 'sentOn',
    exist: 'sentOn',
    mode: [TECHNICIAN, SALES, PARTS],
    label: <FormattedMessage id="page.stats.sendDate" defaultMessage="Send date" />,
    transform: (from: string, to: string) => ({ sentFrom: from, sentTo: to })
  },
  DEFAULT: {
    sort: 'startDate',
    transform: (from: string, to: string) => ({ startDateFrom: from, startDateTo: to })
  }
};

// Maps BO dateField values to API grouping column prefixes.
// sendDate → sentOn because the API column is named after the event (sentOn), not the filter concept (sendDate).
export const DATE_FIELD_API_PREFIX: Record<string, string> = {
  [DATE_FIELDS.CREATED_ON]: 'createdOn',
  [DATE_FIELDS.SEND]: 'sentOn',
  [DATE_FIELDS.START]: 'startDate',
};

export const STATISTICS_URL = createDictionary({
  [PARTS]: PARTS_INTERVENTION_STATISTICS_URL
}, INTERVENTIONS_STATISTICS_URL);

export const CHAT_STATISTICS_URL_BY_MODE = createDictionary({
  [PARTS]: PARTS_CHAT_STATISTICS_URL
}, CHAT_STATISTICS_URL);

export const CHECKLIST_ADVICE_URL = createDictionary({
  [PARTS]: PARTS_CHECKLIST_ADVICE_STATISTICS_URL
}, CHECK_LIST_ADVICE_STATISTICS_URL);

export const SELF_SERVICE_STATUS = 'SELF_SERVICE_STATUS';
export const SELF_SERVICE_QUESTIONS = 'SELF_SERVICE_QUESTIONS';
export const SELF_SERVICE_EXTRAS = 'SELF_SERVICE_EXTRAS';
export const SELF_SERVICE_PROACTIVE_WORKS = 'SELF_SERVICE_PROACTIVE_WORKS';
export const SELF_SERVICE_TOP_CONTRIBUTORS = 'SELF_SERVICE_TOP_CONTRIBUTORS';
export const SELF_SERVICE_TOP_CONTRIBUTORS_DEFAULT_SORT = 'checkOutSent,desc';

export const SELF_SERVICE_STATS_TABS = {
  SELF_SERVICE_STATUS,
  SELF_SERVICE_QUESTIONS,
  SELF_SERVICE_EXTRAS,
  SELF_SERVICE_PROACTIVE_WORKS,
  SELF_SERVICE_TOP_CONTRIBUTORS
};

export const EXPORT_DATA_TYPE = {
  EXTRA: 'EXTRA',
  PROACTIVE_WORKS: 'PROACTIVE_WORKS'
};
